import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import StandardPricingForm from './StandardPricingForm';
import MultiTierForm from './MultiTierForm';
import { 
  CurrencyDollarIcon,
  PlusIcon,
  PencilIcon,
  ArrowLeftIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

const TiersList = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStandardForm, setShowStandardForm] = useState(false);
  const [showMultiTierForm, setShowMultiTierForm] = useState(false);
  const [isEditingStandard, setIsEditingStandard] = useState(false);

  useEffect(() => {
    fetchData();
  }, [itemId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [itemResponse, tiersResponse] = await Promise.all([
        api.get(`/price-book/items/${itemId}`),
        api.get(`/price-book/items/${itemId}/tiers`)
      ]);
      
      setItem(itemResponse.data);
      setTiers(tiersResponse.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleStandardPricingSave = () => {
    setShowStandardForm(false);
    setIsEditingStandard(false);
    fetchData(); // Refresh data
  };

  const handleMultiTierSave = () => {
    setShowMultiTierForm(false);
    fetchData(); // Refresh data
  };

  const openStandardPricing = (isEditing = false) => {
    setIsEditingStandard(isEditing);
    setShowStandardForm(true);
  };

  const openMultiTierForm = () => {
    setShowMultiTierForm(true);
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const formatQuantityRange = (min, max) => {
    if (!max) return `${min}+`;
    return `${min} - ${max}`;
  };

  const renderActionButtons = () => {
    if (!item) return null;

    if (item.pricing_type === 'standard') {
      return (
        <div className="flex space-x-3">
          {tiers.length > 0 ? (
            <button
              onClick={() => openStandardPricing(true)}
              className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <PencilIcon className="-ml-1 mr-2 h-5 w-5" />
              Edit Pricing
            </button>
          ) : (
            <button
              onClick={() => openStandardPricing(false)}
              className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Add Pricing
            </button>
          )}
        </div>
      );
    } else {
      // Tiered pricing
      return (
        <div className="flex space-x-3">
          <button
            onClick={openMultiTierForm}
            className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <PencilIcon className="-ml-1 mr-2 h-5 w-5" />
            Manage Tiers
          </button>
          <button
            onClick={openMultiTierForm}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Tier
          </button>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/price-book/items')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900">Price Tiers</h1>
            <p className="mt-2 text-sm text-gray-700">
              {item && (
                <>
                  Managing price tiers for: <span className="font-medium">{item.item_name}</span>
                </>
              )}
            </p>
          </div>
          {renderActionButtons()}
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Item Details Card */}
      {item && (
        <div className="bg-white shadow rounded-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Item Details</h3>
              <div className="mt-2">
                <div className="text-lg font-medium text-gray-900">{item.item_name}</div>
                <div className="text-sm text-gray-500">{item.category_display_label}</div>
                {item.description && (
                  <div className="text-sm text-gray-600 mt-1">{item.description}</div>
                )}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Unit & Type</h3>
              <div className="mt-2">
                <div className="text-sm text-gray-900">
                  {item.unit_display_label} {item.unit_abbreviation && `(${item.unit_abbreviation})`}
                </div>
                <div className="text-sm text-gray-600">
                  {item.is_recurring ? 'Recurring' : 'One-time'}
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Effective Period</h3>
              <div className="mt-2">
                <div className="text-sm text-gray-900">
                  {new Date(item.effective_start_date).toLocaleDateString()}
                  {item.effective_end_date ? 
                    ` - ${new Date(item.effective_end_date).toLocaleDateString()}` : 
                    ' - Ongoing'
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tiers Table */}
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {tiers.length === 0 ? (
                <div className="bg-white px-6 py-14 text-center">
                  <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {item?.pricing_type === 'standard' ? 'No pricing configured' : 'No price tiers found'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {item?.pricing_type === 'standard' 
                      ? 'Get started by setting up pricing for this item.'
                      : 'Get started by creating the first price tier for this item.'
                    }
                  </p>
                  <div className="mt-6">
                    {item?.pricing_type === 'standard' ? (
                      <button
                        onClick={() => openStandardPricing(false)}
                        className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                        Add Pricing
                      </button>
                    ) : (
                      <button
                        onClick={openMultiTierForm}
                        className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                        Add First Tier
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Tier Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Quantity Range
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Base Price
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Per Unit Price
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Setup Fee
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Tier Price
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Billing Frequency
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tiers.map((tier) => (
                      <tr key={tier.id} className={`${!tier.is_active ? 'bg-gray-50 opacity-75' : 'bg-white'} hover:bg-gray-50`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-3" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {tier.tier_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                Sort: {tier.sort_order}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatQuantityRange(tier.min_quantity, tier.max_quantity)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatPrice(tier.base_price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatPrice(tier.per_unit_price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatPrice(tier.setup_fee)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatPrice(tier.tier_price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {tier.frequency_display_label}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {tier.is_active ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <EyeSlashIcon className="w-3 h-3 mr-1" />
                              Inactive
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      {tiers.length > 0 && (
        <div className="mt-6 text-sm text-gray-500">
          {tiers.length} price tier{tiers.length !== 1 ? 's' : ''} configured
        </div>
      )}

      {/* Modals */}
      {showStandardForm && (
        <StandardPricingForm
          item={item}
          isEditing={isEditingStandard}
          onSave={handleStandardPricingSave}
          onCancel={() => setShowStandardForm(false)}
        />
      )}

      {showMultiTierForm && (
        <MultiTierForm
          item={item}
          onSave={handleMultiTierSave}
          onCancel={() => setShowMultiTierForm(false)}
        />
      )}
    </div>
  );
};

export default TiersList; 